export type QuestionSetSummaryConfig = {
    enableFeature: boolean;
    optionMenuDisplay: boolean;
    download: boolean;
    columns: Record<string, boolean>;
};

export const shouldShowQuestionSetSummaryMenu = (isQuestionSetContext: boolean, config: QuestionSetSummaryConfig): boolean => (
    isQuestionSetContext
    && config?.enableFeature === true
    && config?.optionMenuDisplay === true
);

type RawQuestionSetSummaryConfig = {
    [key: string]: unknown;
};

const DEFAULT_CONFIG: QuestionSetSummaryConfig = {
    enableFeature: false,
    optionMenuDisplay: false,
    download: false,
    columns: {},
};

const parseBoolean = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();
        if (normalizedValue === 'true') {
            return true;
        }
        if (normalizedValue === 'false') {
            return false;
        }
    }
    return false;
};

const hasSummaryConfigKeys = (input: Record<string, unknown>): boolean => {
    const normalizedKeys = new Set(Object.keys(input).map((key) => key.trim().toLowerCase()));

    return normalizedKeys.has('enable_feature')
        || normalizedKeys.has('option_menu_display')
        || normalizedKeys.has('download');
};

const toRawConfig = (input: unknown): RawQuestionSetSummaryConfig | null => {
    if (Array.isArray(input)) {
        const configObject = input
            .filter((entry) => !!entry && typeof entry === 'object')
            .find((entry) => hasSummaryConfigKeys(entry as Record<string, unknown>));

        return (configObject as RawQuestionSetSummaryConfig) || null;
    }

    if (input && typeof input === 'object') {
        if (hasSummaryConfigKeys(input as Record<string, unknown>)) {
            return input as RawQuestionSetSummaryConfig;
        }
        return null;
    }
    return null;
};

export const getQuestionSetSummaryConfig = (): QuestionSetSummaryConfig => {
    const rawConfig = window.marketplaceProperties?.QUESTION_SET_SUMMARY_CONFIG;

    if (!rawConfig) {
        return DEFAULT_CONFIG;
    }

    let parsedConfig: unknown = rawConfig;
    if (typeof rawConfig === 'string') {
        try {
            parsedConfig = JSON.parse(rawConfig);
        } catch {
            return DEFAULT_CONFIG;
        }
    }

    const configObject = toRawConfig(parsedConfig);
    if (!configObject) {
        return DEFAULT_CONFIG;
    }
    const getConfigValue = (key: string) => {
        const exactValue = configObject[key];

        if (exactValue !== undefined) {
            return exactValue;
        }
        const normalizedKey = key.toLowerCase();
        const matchedKey = Object.keys(configObject).find((candidateKey) => candidateKey.trim().toLowerCase() === normalizedKey);

        return matchedKey ? configObject[matchedKey] : undefined;
    };

    return {
        enableFeature: parseBoolean(getConfigValue('enable_feature')),
        optionMenuDisplay: parseBoolean(getConfigValue('option_menu_display')),
        download: parseBoolean(getConfigValue('download')),
        columns: Object.entries(
            getConfigValue('columns') && typeof getConfigValue('columns') === 'object'
                && !Array.isArray(getConfigValue('columns'))
                ? getConfigValue('columns') as Record<string, unknown>
                : {}
        ).reduce<Record<string, boolean>>((result, [key, value]) => {
            result[key] = parseBoolean(value);
            return result;
        }, {}),
    };
};
